// ============================================================================
// TriggerTaskConfirmModal
// Always-confirm step for a trigger_task action — never one-click like a
// requirement-write action, since a task run takes minutes and isn't
// instantly reversible once started.
// ============================================================================

import React, { useState, useEffect } from 'react';
import { X, PlayCircle } from 'lucide-react';
import { AvatarRecommendedAction } from '../../../types/avatarActions';
import { AvatarTriggerableTask } from './avatarTriggerableTasks';

interface TriggerTaskConfirmModalProps {
  action: AvatarRecommendedAction;
  task: AvatarTriggerableTask;
  instruction: string;
  onConfirm: (instruction: string) => Promise<void>;
  onClose: () => void;
}

export function TriggerTaskConfirmModal({ action, task, instruction: initialInstruction, onConfirm, onClose }: TriggerTaskConfirmModalProps) {
  const [instruction, setInstruction] = useState(initialInstruction);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose]);

  const handleConfirm = async () => {
    if (!instruction.trim()) return;
    setSaving(true);
    try {
      await onConfirm(instruction.trim());
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#e2e8f0]">
          <h2 className="text-base font-bold text-navy flex items-center gap-2">
            <PlayCircle size={18} className="text-primary" /> Run: {task.name}
          </h2>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 transition-colors">
            <X size={16} />
          </button>
        </div>

        <div className="overflow-y-auto flex-1 p-6 flex flex-col gap-4">
          {action.rationale && (
            <p className="text-xs text-slate-500 italic">{action.rationale}</p>
          )}
          <p className="text-xs text-slate-500">{task.description}</p>
          <p className="text-xs text-slate-400">Estimated 1–2 min. Runs as an n8n task — not instant, not reversible once started.</p>

          <div>
            <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">Instruction</label>
            <textarea
              value={instruction}
              onChange={e => setInstruction(e.target.value)}
              rows={5}
              placeholder="Describe exactly what to generate"
              className="w-full border border-[#e2e8f0] rounded-xl px-3 py-2.5 text-sm text-navy placeholder:text-slate-400 focus:ring-2 focus:ring-primary focus:border-transparent outline-none resize-none"
            />
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-[#e2e8f0] bg-slate-50/50">
          <button
            onClick={onClose}
            className="flex items-center gap-1.5 rounded-xl h-9 px-4 border border-slate-200 bg-white text-slate-600 text-sm font-semibold hover:bg-slate-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={saving || !instruction.trim()}
            className="flex items-center gap-1.5 rounded-xl h-9 px-5 bg-primary hover:bg-primary-dark transition-colors text-white text-sm font-bold shadow-sm disabled:opacity-60"
          >
            {saving ? 'Starting…' : 'Run task'}
          </button>
        </div>
      </div>
    </div>
  );
}
